import { useCallback, useState } from 'react';
import {
  Form,
  Field,
  FormSizes,
  TextInput,
  Checkbox,
  Button,
  ButtonVariants,
  ButtonSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  SocialLoginButton,
  SocialLoginProviders,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
  FormValidateOn,
} from '@carboledger/ui';
import { loginValidation } from '../authValidation';
import styles from './LoginForm.module.scss';
import { useTranslations } from '@/hooks';

/**
 * Props:
 *   onSubmit        ({ email, password }) => void
 *   isLoading       true while the credentials call is in flight
 *   error           RTK Query error from that call, or undefined
 *   onSocialLogin   (provider: 'google' | 'microsoft') => void — SSO click
 *   onForgotPassword  () => void
 */
export const LoginForm = ({ onSubmit, isLoading, error, onSocialLogin, onForgotPassword }) => {
  const t = useTranslations();
  const [showPassword, setShowPassword] = useState(false);

  const handleShowPassword = useCallback((event) => {
    setShowPassword(event.target.checked);
  }, []);
  const handleMicrosoftLogin = useCallback(() => {
    onSocialLogin?.(SocialLoginProviders.microsoft);
  }, [onSocialLogin]);
  const handleGoogleLogin = useCallback(() => {
    onSocialLogin?.(SocialLoginProviders.google);
  }, [onSocialLogin]);

  return (
    <Form
      validateOn={FormValidateOn.change}
      initialValues={{ email: '', password: '' }}
      onSubmit={onSubmit}
      size={FormSizes.small}
      className={styles.root}
    >
      {() => (
        <>
          <Typography
            variant={TypographyVariants.h6}
            as={TypographyElements.h1}
            className={styles.heading}
          >
            {t('AUTH_LOGIN_TITLE')}
          </Typography>

          {error && (
            <Alert
              variant={AlertVariants.danger}
              appearance={AlertAppearances.accent}
              orientation={AlertOrientations.horizontal}
              className={styles.alert}
            >
              {error.data?.message ?? t('AUTH_INVALID_CREDENTIALS')}
            </Alert>
          )}

          <div className={styles.fields}>
            <Field name="email" required rules={loginValidation.email}>
              {({ id, name, value, onChange, onBlur, status, message, required }) => (
                <TextInput
                  id={id}
                  name={name}
                  label={t('AUTH_EMAIL_LABEL')}
                  placeholder={t('AUTH_EMAIL_PLACEHOLDER')}
                  type="email"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  status={status}
                  message={message}
                  required={required}
                />
              )}
            </Field>

            <div className={styles.passwordField}>
              <Field name="password" required>
                {({ id, name, value, onChange, onBlur, status, message, required }) => (
                  <TextInput
                    id={id}
                    name={name}
                    label={t('AUTH_PASSWORD_LABEL')}
                    placeholder={t('AUTH_PASSWORD_PLACEHOLDER')}
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    status={status}
                    message={message}
                    required={required}
                  />
                )}
              </Field>
              <div className={styles.passwordRow}>
                <Checkbox
                  label={t('AUTH_SHOW_PASSWORD')}
                  checked={showPassword}
                  onChange={handleShowPassword}
                />
                <Button
                  variant={ButtonVariants.tertiary}
                  size={ButtonSizes.sm}
                  className={styles.linkButton}
                  type="button"
                  onClick={onForgotPassword}
                >
                  {t('AUTH_FORGOT_PASSWORD_LINK')}
                </Button>
              </div>
            </div>
          </div>

          <Button type="submit" fullWidth size={ButtonSizes.sm} loading={isLoading}>
            {t('AUTH_LOGIN_ACTION')}
          </Button>

          <div className={styles.divider}>
            <span className={styles.dividerLine} />
            <Typography
              variant={TypographyVariants.body}
              color={TypographyColors.secondary}
              as={TypographyElements.span}
            >
              {t('AUTH_OR')}
            </Typography>
            <span className={styles.dividerLine} />
          </div>

          <div className={styles.sso}>
            <SocialLoginButton
              provider={SocialLoginProviders.microsoft}
              onClick={handleMicrosoftLogin}
            >
              {t('AUTH_LOGIN_MICROSOFT')}
            </SocialLoginButton>
            <SocialLoginButton provider={SocialLoginProviders.google} onClick={handleGoogleLogin}>
              {t('AUTH_LOGIN_GOOGLE')}
            </SocialLoginButton>
          </div>
        </>
      )}
    </Form>
  );
};
